import { useQuery } from '@tanstack/react-query';
import {
  formatMoney,
  fromCents,
  type LedgerCycle,
  SQUARING_STATE_LABELS,
  SQUARING_STATES,
  SQUARING_TOLERANCE,
  type SquaringResponse,
  type SquaringRow,
  type SquaringState,
  toCents,
} from '@k-management/shared';
import { Fragment, useState } from 'react';
import { Link } from 'react-router';
import { Icon } from '../../components/Icon.js';
import { amountSort, SortHeader, useTableSort } from '../../components/SortHeader.js';
import { apiRequest } from '../../lib/api.js';
import { formatDate } from '../../lib/format.js';
import { useSessionValue } from '../../lib/preferences.js';
import { CreditNoteMark } from './treasury-ui.js';

/**
 * Tesoreria › Mastrini › Quadratura (12/10/2026): at 31 December of the year,
 * each subject's balance in the ledger against what is open in the Tesoreria
 * at that day; opening a row shows the invoices that do not agree.
 */
export function LedgerSquaringView({
  year,
  onOpenMatches,
}: {
  year: number;
  /** To the matches of the year, looking for the subject. */
  onOpenMatches: (name: string) => void;
}) {
  const [state, setState] = useSessionValue<SquaringState>(
    'list.ledger-squaring.state',
    'differenza',
  );
  const [cycle, setCycle] = useSessionValue<LedgerCycle>('list.ledger-squaring.cycle', 'attivo');
  const [query, setQuery] = useSessionValue('list.ledger-squaring.query', '');
  const [open, setOpen] = useState<Set<string>>(new Set());
  const data = useQuery({
    queryKey: ['treasury', 'ledgers', year, 'squaring'],
    queryFn: () => apiRequest<SquaringResponse>(`/treasury/ledgers/${year}/squaring`),
  });

  const ofCycle = (data.data?.rows ?? []).filter((row) => row.cycle === cycle);
  const needle = query.trim().toLowerCase();
  const rows = ofCycle.filter(
    (row) =>
      row.state === state &&
      (!needle || `${row.name} ${row.subject?.name ?? ''}`.toLowerCase().includes(needle)),
  );
  // The details of a subject stay under its row, whatever the order.
  const sorting = useTableSort('ledger-squaring', rows, {
    subject: (row) => row.subject?.name ?? row.name,
    ledger: (row) => amountSort(row.ledger),
    treasury: (row) => amountSort(row.treasury),
    difference: (row) => amountSort(row.difference),
  });
  const total = (list: SquaringRow[], key: 'ledger' | 'treasury' | 'difference') =>
    fromCents(list.reduce((sum, row) => sum + toCents(row[key]), 0));
  const rowKey = (row: SquaringRow) => row.accountId ?? `${row.cycle}|${row.name}`;

  return (
    <div className="stack">
      <p className="muted small">
        Al 31/12/{year}, per ogni soggetto: il saldo del mastrino del commercialista e quanto è
        aperto in Tesoreria a quella data (fatture fino al 31/12 meno gli incassi e i pagamenti fino
        al 31/12). Differenze fino a {formatMoney(SQUARING_TOLERANCE)} contano come arrotondamento.
        Le pro forma non entrano; le autofatture ricevute sì, per l’imponibile. Apri un soggetto per
        vedere le fatture che non tornano.
      </p>
      <div className="treasury-tiles">
        <div className="treasury-tile">
          <span>Saldo dei mastrini</span>
          <strong>{formatMoney(total(ofCycle, 'ledger'))}</strong>
        </div>
        <div className="treasury-tile">
          <span>Aperto in Tesoreria</span>
          <strong>{formatMoney(total(ofCycle, 'treasury'))}</strong>
        </div>
        {SQUARING_STATES.map((key) => {
          const list = ofCycle.filter((row) => row.state === key);
          return (
            <button
              key={key}
              type="button"
              className={`treasury-tile${state === key ? ' selected' : ''}`}
              onClick={() => setState(key)}
            >
              <span>{SQUARING_STATE_LABELS[key]}</span>
              <strong>{list.length}</strong>
              {key === 'differenza' && (
                <small className="muted">{formatMoney(total(list, 'difference'))} in tutto</small>
              )}
            </button>
          );
        })}
      </div>

      <div className="ledger-toolbar">
        <div className="segmented-small" role="group" aria-label="Ciclo">
          {(['attivo', 'passivo'] as const).map((key) => (
            <button
              key={key}
              type="button"
              className={cycle === key ? 'selected' : ''}
              onClick={() => setCycle(key)}
            >
              {key === 'attivo' ? 'Clienti' : 'Fornitori'}
            </button>
          ))}
        </div>
        <label className="search-box">
          <Icon name="search" />
          <input
            value={query}
            placeholder="Soggetto"
            onChange={(event) => setQuery(event.target.value)}
          />
        </label>
      </div>
      {data.isError && <p className="error">{data.error.message}</p>}

      <div className="table-wrap">
        <table className="data-table ledger-table">
          <thead>
            <tr>
              <SortHeader sort={sorting} column="subject">
                Soggetto
              </SortHeader>
              <SortHeader sort={sorting} column="ledger" numeric>
                Saldo mastrino
              </SortHeader>
              <SortHeader sort={sorting} column="treasury" numeric>
                Aperto in Tesoreria
              </SortHeader>
              <SortHeader sort={sorting} column="difference" numeric>
                Differenza
              </SortHeader>
            </tr>
          </thead>
          <tbody>
            {sorting.rows.map((row) => {
              const key = rowKey(row);
              const expanded = open.has(key);
              const detail = row.onlyTreasury.length + row.onlyLedger.length > 0;
              return (
                <Fragment key={key}>
                  <tr
                    className={detail ? 'openable' : undefined}
                    onClick={(event) => {
                      if (!detail || (event.target as HTMLElement).closest('button, a')) return;
                      const next = new Set(open);
                      if (expanded) next.delete(key);
                      else next.add(key);
                      setOpen(next);
                    }}
                  >
                    <td>
                      {detail && <span aria-hidden="true">{expanded ? '▾' : '▸'} </span>}
                      {row.subject ? (
                        <Link to={`/anagrafica/${row.subject.id}`}>{row.subject.name}</Link>
                      ) : (
                        <strong>{row.name}</strong>
                      )}
                      {row.accountId === null && (
                        <small className="muted block">non c’è nel mastrino {year}</small>
                      )}
                      {row.state === 'differenza' && row.accountId !== null && (
                        <button
                          type="button"
                          className="link small block"
                          onClick={() => onOpenMatches(row.subject?.name ?? row.name)}
                        >
                          Vedi negli abbinamenti
                        </button>
                      )}
                    </td>
                    <td className="numeric">{formatMoney(row.ledger)}</td>
                    <td className="numeric">{formatMoney(row.treasury)}</td>
                    <td className="numeric">
                      <strong className={row.state === 'differenza' ? 'warn-text' : undefined}>
                        {formatMoney(row.difference)}
                      </strong>
                    </td>
                  </tr>
                  {expanded && <DetailRows row={row} year={year} />}
                </Fragment>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={4} className="muted">
                  {data.isPending ? 'Caricamento…' : 'Nessun soggetto con questi filtri.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DetailRows({ row, year }: { row: SquaringRow; year: number }) {
  return (
    <>
      {row.onlyTreasury.map((item) => (
        <tr key={item.invoiceId} className="ledger-entry">
          <td>
            Aperta in Tesoreria: <CreditNoteMark kind={item.kind} />
            <Link to={`/tesoreria/fatture/${item.invoiceId}`}>{item.number}</Link> del{' '}
            {formatDate(item.date)}
            <small className="muted block">
              {item.ledgerOpen === null
                ? `nel mastrino non è aperta al 31/12/${year}: probabilmente un incasso o pagamento da abbinare`
                : `nel mastrino resta aperta per ${formatMoney(item.ledgerOpen)}`}
            </small>
          </td>
          <td className="numeric">
            {item.ledgerOpen === null ? '' : formatMoney(item.ledgerOpen)}
          </td>
          <td className="numeric">{formatMoney(item.open)}</td>
          <td />
        </tr>
      ))}
      {row.onlyLedger.map((item, index) => (
        <tr key={`ledger-${index}`} className="ledger-entry">
          <td>
            Aperta nel mastrino:{' '}
            {item.documentNumber
              ? `${item.documentNumber}${item.documentDate ? ` del ${formatDate(item.documentDate)}` : ''}`
              : (item.description ?? 'senza numero')}
            <small className="muted block">
              in Tesoreria non è aperta: già saldata qui, oppure non c’è (manca da Gestify?)
            </small>
          </td>
          <td className="numeric">{formatMoney(item.open)}</td>
          <td />
          <td />
        </tr>
      ))}
    </>
  );
}
